import type { FC } from "preact/compat";
import { Link } from "react-router-dom";
import { useApp } from "~/core";
import { IconButton } from "~/components";
import { useBreakpoint } from "~/hooks";
import {
    DiscoveryGlobalFallback,
    DiscoveryItem,
    Search,
    Tracker,
    TrackerPageMobile,
} from "~/organisms";
import MobileHeadline from "./MobileHeadline";
import { MobileMenu } from "./MobileMenu";
import ChevronLeft from "@material-design-icons/svg/outlined/chevron_left.svg";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import HamburgerIcon from "@material-design-icons/svg/outlined/menu.svg";
import ArrowIcon from "~/assets/arrow.svg";
import EmblemIcon from "~/assets/emblem.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const Header: FC = () => {
    const { breadcrumbs, mobile, status, dialog } = useApp();
    const bpMd = useBreakpoint("md");
    const parts = breadcrumbs.items.value;
    const MenuIcon =
        mobile.menuIcon.value === "back" ? ChevronLeft : HamburgerIcon;
    const menuTitle =
        mobile.menuIcon.value === "back" ? "Go back" : "Toggle menu";

    return (
        <header
            className={cn(
                "relative flex h-[50px] min-h-[50px] w-full items-center bg-gradient-primary-to-l py-2 transition-transform duration-200",
                status.lost.value && "grayscale",
                mobile.isHeaderHidden.value &&
                    "-translate-y-[calc(100%+var(--safe-top))]",
                dialog.isMenuInert.value && "pointer-events-none",
            )}
        >
            {bpMd.value ? (
                <>
                    <Search />
                    <Tracker />
                </>
            ) : (
                <>
                    <MobileMenu />
                    <TrackerPageMobile />
                </>
            )}
            <div className="absolute bottom-0 left-[9px] hidden w-[32px] border-b border-white border-opacity-25 md:block" />
            <div className="flex min-w-[50px] justify-center p-2 md:border-r md:border-white md:border-opacity-25">
                <DiscoveryGlobalFallback />
                {status.loading.value ? (
                    <Loader width="20" height="20" className="text-sky-500" />
                ) : (
                    <>
                        <EmblemIcon className="hidden w-[30px] md:block" />
                        <IconButton
                            flat
                            title={menuTitle}
                            aria-label={menuTitle}
                            className="text-white md:hidden"
                            onClick={mobile.onMenuAction}
                        >
                            {mobile.isMenuOpen.value ? (
                                <CloseIcon
                                    width="24"
                                    height="24"
                                    className="fill-white"
                                />
                            ) : (
                                <MenuIcon
                                    width="24"
                                    height="24"
                                    className="fill-white"
                                />
                            )}
                        </IconButton>
                    </>
                )}
            </div>
            <div className="w-full md:w-auto md:pl-1">
                <MobileHeadline />
                <nav aria-label="Breadcrumb" className="hidden md:block">
                    <ul className="flex items-center">
                        {parts.map(
                            ({ name, link, content, discovery }, index) => (
                                <li
                                    key={name}
                                    data-discovery-target="v=breadcrumb"
                                    className={cn(
                                        "flex items-center px-1 text-white",
                                        discovery && "relative",
                                    )}
                                >
                                    {link ? (
                                        <Link
                                            title={content ? name : undefined}
                                            className="outline-white hover:text-sky-400"
                                            to={link}
                                        >
                                            {content ?? name}
                                        </Link>
                                    ) : (
                                        <span
                                            className="text-yellow-400"
                                            aria-current="page"
                                        >
                                            {content ?? name}
                                        </span>
                                    )}
                                    {index < parts.length - 1 && (
                                        <ArrowIcon className="ml-2 -rotate-90" />
                                    )}
                                    {discovery && (
                                        <DiscoveryItem
                                            tag={discovery.tag}
                                            inline
                                            className="ml-2"
                                            maxWidth={discovery.maxWidth}
                                            maxHeight={discovery.maxHeight}
                                        />
                                    )}
                                </li>
                            ),
                        )}
                    </ul>
                </nav>
            </div>
        </header>
    );
};

export default Header;
